// Pieces every Sales portal screen shares: the context (data source, the
// viewer's role, copy), error copy keyed on SQLSTATE + the server's known
// messages, chips, CSV download and the agreement arithmetic.

import { createContext, useContext, type ReactNode } from "react";
import type { Locale } from "../../i18n/dictionary";
import type { SalesDictionary } from "../../i18n/salesDictionary";
import { roleCan, type SalesAction, type SalesDataSource, type SalesMe, type SalesRole } from "../../data/sales";
import type { InvoiceStatus, OrgLifecycle } from "../../data/types";
import { PortalWriteError } from "../../data/supabaseAdapter";
import { Notice, type Tone } from "../admin/ui";

export interface SalesCtx {
  ds: SalesDataSource;
  me: SalesMe;
  t: SalesDictionary;
  locale: Locale;
  demo: boolean;
}

const Ctx = createContext<SalesCtx | null>(null);

export function SalesProvider({ value, children }: { value: SalesCtx; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSales(): SalesCtx & { can: (a: SalesAction) => boolean } {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSales outside SalesProvider");
  return { ...v, can: (a: SalesAction) => roleCan(v.me.role, a) };
}

/** The server's P0001/P0002 messages we know, mapped to their copy. */
const KNOWN: [RegExp, keyof SalesDictionary["common"]][] = [
  [/last owner/i, "errLastOwner"],
  [/not signed in to Zooylo/i, "errNotSignedIn"],
  [/why it was lost/i, "errLostReason"],
  [/once it has ended/i, "errMonthNotEnded"],
  [/share cannot be more/i, "errShareAboveRetail"],
  [/SST/i, "errSstRange"],
];

/**
 * Copy for a failed write. Keyed on the SQLSTATE; a rule the server explains
 * (P0001 / P0002) shows the server's own reason, translated when it is known.
 */
export function salesErrorCopy(t: SalesDictionary, err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  const message = err instanceof Error ? err.message : "";
  if (code === "42501") return t.common.errNotAuth;
  if (code === "P0001" || code === "P0002") {
    for (const [re, key] of KNOWN) if (re.test(message)) return t.common[key];
    if (code === "P0002") return message || t.common.errNotFound;
    return message || t.common.errInvalid;
  }
  if (code === "22P02" || code === "23514") return t.common.errInvalid;
  return t.common.errGeneric;
}

export function statusChip(t: SalesDictionary, s: OrgLifecycle): { tone: Tone; label: string } {
  return s === "active"
    ? { tone: "good", label: t.common.stActive }
    : s === "suspended"
      ? { tone: "attention", label: t.common.stSuspended }
      : { tone: "muted", label: t.common.stClosed };
}

export function invoiceChip(t: SalesDictionary, s: InvoiceStatus): { tone: Tone; label: string } {
  switch (s) {
    case "paid":
      return { tone: "good", label: t.invoices.stPaid };
    case "issued":
      return { tone: "info", label: t.invoices.stIssued };
    case "void":
      return { tone: "muted", label: t.invoices.stVoid };
    default:
      return { tone: "neutral", label: t.invoices.stDraft };
  }
}

export function roleLabel(t: SalesDictionary, r: SalesRole): string {
  return t.roles[r];
}

/** Retail − share for the read-only "Organisation keeps" field. Never below 0. */
export function keeps(retail: string, share: string): number {
  const r = Number(retail);
  const s = Number(share);
  if (!Number.isFinite(r) || !Number.isFinite(s)) return 0;
  return Math.max(Math.round((r - s) * 100) / 100, 0);
}

/** The first problem with retail / share / SST as typed, or "". Mirrors the SQL. */
export function shareProblem(t: SalesDictionary, retail: string, share: string, sst: string): string {
  const r = retail.trim() === "" ? 0 : Number(retail);
  const s = share.trim() === "" ? 0 : Number(share);
  const rate = sst.trim() === "" ? 0 : Number(sst);
  if (![r, s, rate].every((n) => Number.isFinite(n) && n >= 0)) return t.common.errInvalid;
  if (rate > 100) return t.common.errSstRange;
  if (r > 0 && s > r) return t.common.errShareAboveRetail;
  return "";
}

/** Hands the browser a CSV file to save. */
export function downloadCsv(filename: string, csv: string): void {
  // A BOM so Excel opens UTF-8 (Malay names, "—") correctly.
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Shown in place of a form's save button when the role cannot change it. */
export function ReadOnlyNote() {
  const { t } = useSales();
  return (
    <p style={{ margin: "10px 0 0", fontSize: 13.5, color: "var(--ink-faint)" }}>{t.shell.readOnly}</p>
  );
}

export function StatusLine({ msg }: { msg: { tone: Tone; text: string } | null }) {
  return (
    <span
      role="status"
      aria-live="polite"
      style={{ fontSize: 14, fontWeight: 600, color: msg?.tone === "good" ? "var(--sage-deep)" : "var(--peach-deep)" }}
    >
      {msg?.text}
    </span>
  );
}

export function SectionTitle({ id, children, action }: { id?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", margin: "0 0 12px" }}>
      <h2 id={id} style={{ fontSize: 22, color: "var(--ink-deep)", margin: 0 }}>
        {children}
      </h2>
      {action}
    </div>
  );
}

export function Forbidden({ text }: { text: string }) {
  return <Notice tone="attention">{text}</Notice>;
}

export const TEXTAREA_ROWS = 3;
